import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Slider,
  Stack,
  IconButton,
  Tooltip,
  Collapse,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Speed as SpeedIcon,
  MyLocation as ZoomIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import { TimelineDwell } from '../services/polService';

export interface DwellLocationGroup {
  key: string;
  location_name: string;
  lat: number;
  lon: number;
  place_type: string | null;
  dwells: TimelineDwell[];
  total_duration: number;
  avg_duration: number;
  entities: string[];
  device_types: string[];
  first_seen: string;
  last_seen: string;
}

interface DwellAnalysisTabProps {
  dwells: TimelineDwell[];
  loading: boolean;
  onDwellClick?: (dwell: TimelineDwell | null) => void;
  onGroupClick?: (group: DwellLocationGroup | null) => void;
}

const DEVICE_COLORS: Record<string, 'success' | 'info' | 'secondary' | 'default'> = {
  phone: 'success',
  watch: 'info',
  vehicle: 'secondary',
};

const formatDuration = (seconds: number) => {
  if (!seconds || seconds <= 0) return '0m';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatDateTime = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatDateShort = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
};

function groupDwellsByLocation(dwells: TimelineDwell[]): DwellLocationGroup[] {
  const PROXIMITY_THRESHOLD = 0.001; // ~100m in degrees
  const groups: DwellLocationGroup[] = [];

  for (const dwell of dwells) {
    if (!dwell.lat || !dwell.lon) continue;

    let matched = false;
    for (const group of groups) {
      const latDiff = Math.abs(group.lat - dwell.lat);
      const lonDiff = Math.abs(group.lon - dwell.lon);
      if (latDiff < PROXIMITY_THRESHOLD && lonDiff < PROXIMITY_THRESHOLD) {
        group.dwells.push(dwell);
        matched = true;
        break;
      }
    }

    if (!matched) {
      groups.push({
        key: `${dwell.lat.toFixed(4)}_${dwell.lon.toFixed(4)}`,
        location_name: dwell.location_name || `${dwell.lat.toFixed(5)}, ${dwell.lon.toFixed(5)}`,
        lat: dwell.lat,
        lon: dwell.lon,
        place_type: dwell.place_type,
        dwells: [dwell],
        total_duration: 0,
        avg_duration: 0,
        entities: [],
        device_types: [],
        first_seen: '',
        last_seen: '',
      });
    }
  }

  for (const group of groups) {
    group.total_duration = group.dwells.reduce((sum, d) => sum + d.duration_seconds, 0);
    group.avg_duration = group.dwells.length ? group.total_duration / group.dwells.length : 0;
    group.entities = Array.from(new Set(group.dwells.map((d) => d.entity_id)));
    group.device_types = Array.from(new Set(group.dwells.map((d) => d.device_type)));
    const times = group.dwells.map((d) => new Date(d.start_time).getTime()).sort((a, b) => a - b);
    group.first_seen = new Date(times[0]).toISOString();
    group.last_seen = new Date(times[times.length - 1]).toISOString();
  }

  groups.sort((a, b) => b.dwells.length - a.dwells.length);
  return groups;
}

const DURATION_MARKS = [
  { value: 0, label: '0' },
  { value: 15, label: '15m' },
  { value: 60, label: '1h' },
  { value: 240, label: '4h' },
  { value: 720, label: '12h' },
];

const DwellAnalysisTab: React.FC<DwellAnalysisTabProps> = ({ dwells, loading, onDwellClick, onGroupClick }) => {
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [minDurationMin, setMinDurationMin] = useState(0);
  const [sortBy, setSortBy] = useState<'visits' | 'duration' | 'recent'>('visits');
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [selectedDwellId, setSelectedDwellId] = useState<number | null>(null);

  const uniqueDeviceTypes = useMemo(
    () => Array.from(new Set(dwells.map((d) => d.device_type))),
    [dwells],
  );

  const filteredDwells = useMemo(() => {
    let list = [...dwells];
    if (deviceFilter !== 'all') list = list.filter((d) => d.device_type.toLowerCase() === deviceFilter);
    if (minDurationMin > 0) list = list.filter((d) => d.duration_seconds >= minDurationMin * 60);
    return list;
  }, [dwells, deviceFilter, minDurationMin]);

  const groups = useMemo(() => {
    const g = groupDwellsByLocation(filteredDwells);
    if (sortBy === 'duration') g.sort((a, b) => b.total_duration - a.total_duration);
    else if (sortBy === 'recent') g.sort((a, b) => new Date(b.last_seen).getTime() - new Date(a.last_seen).getTime());
    return g;
  }, [filteredDwells, sortBy]);

  useEffect(() => {
    setSelectedDwellId(null);
    setExpandedGroup(null);
  }, [dwells]);

  const stats = useMemo(() => {
    let totalDuration = 0;
    let longest = 0;
    filteredDwells.forEach((d) => {
      totalDuration += d.duration_seconds;
      if (d.duration_seconds > longest) longest = d.duration_seconds;
    });
    return {
      count: filteredDwells.length,
      locations: groups.length,
      totalDuration,
      avg: filteredDwells.length ? totalDuration / filteredDwells.length : 0,
      longest,
      entities: Array.from(new Set(filteredDwells.map((d) => d.entity_id))).length,
    };
  }, [filteredDwells, groups]);

  const handleGroupZoom = (group: DwellLocationGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    onGroupClick?.(group);
  };

  const handleDwellClick = (dwell: TimelineDwell) => {
    if (selectedDwellId === dwell.dwell_id) {
      setSelectedDwellId(null);
      onDwellClick?.(null);
    } else {
      setSelectedDwellId(dwell.dwell_id);
      onDwellClick?.(dwell);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={28} />
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>Loading dwells…</Typography>
      </Box>
    );
  }

  if (dwells.length === 0) {
    return (
      <Box sx={{ p: 1.5 }}>
        <EmptyState
          dense
          icon={<LocationIcon />}
          title="No dwell data yet"
          description="Choose a domain, entity, and date range for the current selection, then wait for the dashboard to load."
        />
      </Box>
    );
  }

  const durationBuckets = [
    { label: '< 15 min', max: 900, color: '#4caf50' },
    { label: '15–60 min', max: 3600, color: '#2196f3' },
    { label: '1–4 hr', max: 14400, color: '#ff9800' },
    { label: '4+ hr', max: Infinity, color: '#f44336' },
  ];
  const bucketCounts = durationBuckets.map((b, i) => ({
    ...b,
    n: filteredDwells.filter((d) => {
      const prev = durationBuckets[i - 1];
      return d.duration_seconds >= (prev ? prev.max : 0) && d.duration_seconds < b.max;
    }).length,
  }));
  const maxBucket = Math.max(1, ...bucketCounts.map((c) => c.n));

  const deviceCounts = uniqueDeviceTypes.map((t) => ({
    type: t,
    count: filteredDwells.filter((d) => d.device_type === t).length,
  }));
  const maxDevice = Math.max(1, ...deviceCounts.map((c) => c.count));

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LocationIcon sx={{ fontSize: 18 }} />
        Dwell Analysis
      </Typography>

      {/* Stats - evenly spread row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.5, mb: 1 }}>
        {[
          { label: 'Dwells', value: stats.count.toLocaleString() },
          { label: 'Locs', value: String(stats.locations) },
          { label: 'Total', value: formatDuration(stats.totalDuration) },
          { label: 'Avg', value: formatDuration(stats.avg) },
          { label: 'Max', value: formatDuration(stats.longest) },
        ].map(({ label, value }) => (
          <Box key={label} sx={{ py: 0.5, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.5rem', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', lineHeight: 1 }}>{label}</Typography>
            <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.75rem', lineHeight: 1.3 }}>{value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Charts - compact inline */}
      <Accordion defaultExpanded={false} sx={{ mb: 0.75, '&::before': { display: 'none' }, boxShadow: 'none', bgcolor: 'transparent', border: '1px solid', borderColor: 'divider', borderRadius: '4px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>Breakdown</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Box>
              {bucketCounts.map((c) => (
                <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Typography variant="caption" sx={{ width: 50, flexShrink: 0, fontSize: '0.55rem' }}>{c.label}</Typography>
                  <Box sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(c.n / maxBucket) * 100}%`, height: '100%', bgcolor: c.color, borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" sx={{ width: 24, textAlign: 'right', fontSize: '0.55rem', fontWeight: 600 }}>{c.n}</Typography>
                </Box>
              ))}
            </Box>
            <Box>
              {deviceCounts.map((c) => (
                <Box key={c.type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                  <Chip label={c.type} size="small" color={DEVICE_COLORS[c.type.toLowerCase()] ?? 'default'} sx={{ fontSize: '0.5rem', height: 14, width: 50, '& .MuiChip-label': { px: 0.4 } }} />
                  <Box sx={{ flex: 1, height: 6, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
                    <Box sx={{ width: `${(c.count / maxDevice) * 100}%`, height: '100%', bgcolor: 'primary.main', borderRadius: 1 }} />
                  </Box>
                  <Typography variant="caption" sx={{ width: 24, textAlign: 'right', fontSize: '0.55rem', fontWeight: 600 }}>{c.count}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Filters - collapsed by default */}
      <Accordion defaultExpanded={false} sx={{ mb: 0.75, '&::before': { display: 'none' }, boxShadow: 'none', bgcolor: 'transparent', border: '1px solid', borderColor: 'divider', borderRadius: '4px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 28, '&.Mui-expanded': { minHeight: 28 }, py: 0, '& .MuiAccordionSummary-content': { my: 0.25 } }}>
          <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>Filters</Typography>
          {(deviceFilter !== 'all' || minDurationMin > 0) && (
            <Chip label="Active" size="small" color="primary" sx={{ ml: 1, height: 14, fontSize: '0.5rem', '& .MuiChip-label': { px: 0.4 } }} />
          )}
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0, pb: 0.75, px: 1.5 }}>
          <Stack spacing={1}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.7rem' }}>Device</InputLabel>
                <Select value={deviceFilter} label="Device" onChange={(e) => setDeviceFilter(e.target.value)} sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}>
                  <MenuItem value="all">All</MenuItem>
                  {uniqueDeviceTypes.map((t) => (
                    <MenuItem key={t} value={t.toLowerCase()}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.7rem' }}>Sort</InputLabel>
                <Select value={sortBy} label="Sort" onChange={(e) => setSortBy(e.target.value as any)} sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}>
                  <MenuItem value="visits">Most Visits</MenuItem>
                  <MenuItem value="duration">Duration</MenuItem>
                  <MenuItem value="recent">Recent</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ px: 0.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem' }}>
                Min Duration: {minDurationMin === 0 ? 'None' : minDurationMin < 60 ? `${minDurationMin}m` : `${(minDurationMin / 60).toFixed(1)}h`}
              </Typography>
              <Slider
                value={minDurationMin}
                onChange={(_e, v) => setMinDurationMin(v as number)}
                min={0}
                max={720}
                step={5}
                marks={DURATION_MARKS}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => v < 60 ? `${v}m` : `${(v / 60).toFixed(1)}h`}
                size="small"
                sx={{ mt: 0, '& .MuiSlider-markLabel': { fontSize: '0.55rem' } }}
              />
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Location Groups */}
      <Box>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom sx={{ fontSize: '0.8rem' }}>
          Locations ({groups.length})
        </Typography>

        <Box sx={{ maxHeight: 'calc(100vh - 380px)', overflow: 'auto' }}>
          {groups.map((group) => {
            const isExpanded = expandedGroup === group.key;
            return (
              <Box
                key={group.key}
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: isExpanded ? 'primary.main' : 'divider',
                  bgcolor: isExpanded ? 'rgba(33, 150, 243, 0.04)' : 'transparent',
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                }}
              >
                {/* Group header */}
                <Box
                  onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
                  sx={{
                    p: 1,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 0.75,
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <LocationIcon sx={{ fontSize: 16, mt: 0.25, color: 'error.main', flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={group.location_name}
                    >
                      {group.location_name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Chip label={`${group.dwells.length} visits`} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }} />
                      <Chip
                        icon={<SpeedIcon sx={{ fontSize: '0.7rem !important' }} />}
                        label={formatDuration(group.total_duration)}
                        size="small"
                        variant="outlined"
                        sx={{ height: 18, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 }, '& .MuiChip-icon': { ml: 0.25 } }}
                      />
                      {group.place_type && (
                        <Chip label={group.place_type} size="small" variant="outlined" color="primary" sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 }, textTransform: 'capitalize' }} />
                      )}
                      {group.entities.length > 1 && (
                        <Chip label={`${group.entities.length} entities`} size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.5 } }} />
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', mt: 0.25, display: 'block' }}>
                      {formatDateShort(group.first_seen)} – {formatDateShort(group.last_seen)} · avg {formatDuration(group.avg_duration)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                    <Tooltip title="Zoom to location">
                      <IconButton size="small" onClick={(e) => handleGroupZoom(group, e)} sx={{ p: 0.25 }}>
                        <ZoomIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    {isExpanded ? <ArrowUpIcon sx={{ fontSize: 16 }} /> : <ArrowDownIcon sx={{ fontSize: 16 }} />}
                  </Box>
                </Box>

                {/* Expanded dwells */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 1, pb: 1 }}>
                    {/* Duration bar for this group */}
                    <Box sx={{ mb: 1, px: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>Duration range</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                          {formatDuration(Math.min(...group.dwells.map(d => d.duration_seconds)))} – {formatDuration(Math.max(...group.dwells.map(d => d.duration_seconds)))}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={100}
                        sx={{ height: 3, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.08)', '& .MuiLinearProgress-bar': { bgcolor: 'error.main' } }}
                      />
                    </Box>

                    {/* Individual dwells */}
                    {group.dwells
                      .sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime())
                      .map((dwell) => (
                      <Box
                        key={dwell.dwell_id}
                        onClick={() => handleDwellClick(dwell)}
                        sx={{
                          p: 0.75,
                          mb: 0.5,
                          borderRadius: 0.5,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: selectedDwellId === dwell.dwell_id ? 'primary.main' : 'transparent',
                          bgcolor: selectedDwellId === dwell.dwell_id ? 'rgba(33, 150, 243, 0.1)' : 'rgba(255,255,255,0.02)',
                          '&:hover': { bgcolor: 'action.hover' },
                          transition: 'all 0.15s',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 500 }}>{dwell.entity_id}</Typography>
                              <Chip
                                label={dwell.device_type}
                                size="small"
                                color={DEVICE_COLORS[dwell.device_type.toLowerCase()] ?? 'default'}
                                sx={{ fontSize: '0.55rem', height: 14, '& .MuiChip-label': { px: 0.5 } }}
                              />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                              <TimeIcon sx={{ fontSize: 10, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                                {formatDateTime(dwell.start_time)}
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem' }}>
                              {formatDuration(dwell.duration_seconds)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}

          {groups.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography variant="caption" color="text.secondary">No dwells match the current filters</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DwellAnalysisTab;
